<template>
  <!-- Always in the document, hidden by visibility rather than by v-if: the
       panel has to slide out as well as in, and a transform alone would leave
       every control in here reachable by Tab while the drawer is shut. -->
  <div class="cart" :class="{ 'cart--open': open }">
    <div class="cart__ground" @click="close"></div>

    <div
      class="cart__panel"
      role="dialog"
      aria-modal="true"
      aria-label="Warenkorb"
    >
      <!-- The scrolling column is inside the panel, not the panel itself, so
           the paired rule can frame the drawer without riding up with the
           list. -->
      <div class="cart__inner">
        <!-- Centred under its own lozenge, with the ✕ set over it: the same
             head the full-page menu wears, so the two overlays read as one
             family. -->
        <div class="cart__head">
          <h2 class="cart__title">Warenkorb</h2>
          <p class="cart__rule" aria-hidden="true">◆</p>
          <button
            ref="close"
            type="button"
            class="cart__close"
            aria-label="Warenkorb schließen"
            @click="close"
          >&times;</button>
        </div>

        <p v-if="!lines.length" class="cart__empty">Dein Warenkorb ist leer.</p>

        <template v-else>
          <ul class="cart__list">
            <li
              v-for="line in lines"
              :key="line.id"
              class="cart__line"
              :class="{ 'is-busy': store.isLineBusy(line.id) }"
            >
              <!-- The garment, not the cut-out figure the floor uses: a person
                   lying down, cropped square, reads as a mistake at this size.
                   Decorative — the name beside it already says which shirt. -->
              <img
                v-if="thumb(line)"
                class="cart__thumb"
                :src="thumb(line).src"
                alt=""
                loading="lazy"
              >

              <div class="cart__detail">
                <p class="cart__name">{{ line.name }}</p>
                <p class="cart__size">
                  Größe {{ line.size }}
                  <span class="cart__price">{{ money(line.lineTotal) }}</span>
                </p>

                <div class="cart__controls">
                  <div class="cart__quantity">
                    <button
                      type="button"
                      class="cart__step"
                      :aria-label="`Menge verringern, ${line.name}, Größe ${line.size}`"
                      :disabled="line.quantity <= 1 || store.isLineBusy(line.id)"
                      @click="store.setQuantity(line.id, line.quantity - 1)"
                    >&minus;</button>
                    <span class="cart__count" aria-live="polite">{{ line.quantity }}</span>
                    <button
                      type="button"
                      class="cart__step"
                      :aria-label="`Menge erhöhen, ${line.name}, Größe ${line.size}`"
                      :disabled="line.quantity >= maxQuantity || store.isLineBusy(line.id)"
                      @click="store.setQuantity(line.id, line.quantity + 1)"
                    >+</button>
                  </div>

                  <button
                    type="button"
                    class="cart__remove"
                    :aria-label="`${line.name}, Größe ${line.size}, entfernen`"
                    :disabled="store.isLineBusy(line.id)"
                    @click="store.removeLine(line.id)"
                  >Entfernen</button>
                </div>
              </div>
            </li>
          </ul>

          <!-- Inside the scrolling column rather than pinned under it: a cart
               this size never needs the total to follow the reader, and a
               pinned bar would eat a third of a phone screen. -->
          <div class="cart__foot">
            <p class="cart__sum">
              <span>Zwischensumme</span>
              <span class="cart__sum-value">{{ money(store.subtotal) }}</span>
            </p>
            <p class="cart__note">Versand und Steuern werden an der Kasse berechnet.</p>

            <a
              v-if="store.checkoutUrl"
              class="cart__checkout"
              :href="store.checkoutUrl"
            >Zur Kasse</a>
          </div>
        </template>

        <p v-if="store.error" class="cart__error" role="alert">{{ store.error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { cartStore, MAX_QUANTITY } from '@/store/cart.js';
import { formatPrice } from '@/config/money.js';
import { lockScroll, unlockScroll, forgetScrollOffset } from '@/store/scrollLock.js';

const LOCK_HOLDER = 'cart-drawer';

export default {
  name: 'CartDrawer',
  data() {
    return { store: cartStore, maxQuantity: MAX_QUANTITY, opener: null };
  },
  computed: {
    open() {
      return this.store.drawerOpen;
    },
    lines() {
      return this.store.lines;
    },
  },
  watch: {
    open: {
      handler(isOpen) {
        if (isOpen) {
          // Recorded synchronously: whatever opened the drawer is the active
          // element right now, and a deferred watcher would run after focus
          // has already moved into the panel.
          this.opener = document.activeElement;
          lockScroll(LOCK_HOLDER);
          // A frame, not a tick: this watcher runs before the render that makes
          // the panel visible, and a visibility:hidden element cannot take
          // focus — the call would fail silently and leave the caret outside
          // the dialog.
          requestAnimationFrame(() => this.$refs.close.focus());
        } else {
          unlockScroll(LOCK_HOLDER);
          this.returnFocus();
        }
      },
      flush: 'sync',
    },
    $route() {
      forgetScrollOffset();
      this.store.closeDrawer();
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    // A lock outlives the component otherwise, and nothing else can release it.
    unlockScroll(LOCK_HOLDER);
  },
  methods: {
    /* A line whose handle this site does not carry has no photograph to show;
       it keeps its row anyway, because the checkout will still charge for it.
       Everything in the catalogue has at least one photographed shot — the
       cut-out is a fallback, not a choice. */
    thumb(line) {
      if (!line.product) return null;
      return line.product.shots[0] || line.product.flat;
    },
    money(amount) {
      return formatPrice(amount);
    },
    close() {
      this.store.closeDrawer();
    },
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      this.close();
    },
    returnFocus() {
      const opener = this.opener;
      this.opener = null;
      // The mobile entry that opened the drawer sits in a menu that has since
      // closed; focusing a detached or hidden node only drops focus on body.
      if (!opener || !opener.isConnected || !opener.offsetParent) return;
      // preventScroll, because the lock has just put the reader back where they
      // were and a focus that scrolls its target into view would undo that.
      opener.focus({ preventScroll: true });
    },
  },
};
</script>

<style scoped>
/* Above the header (100), the footer and the product page's zoom (200): the
   drawer is the topmost thing on the floor while it is out. It clips its own
   panel so the parked position cannot widen the document. */
.cart {
  position: fixed;
  inset: 0;
  z-index: 300;
  overflow: hidden;

  visibility: hidden;
  pointer-events: none;
  transition: visibility 0s linear 0.32s;
}

.cart--open {
  visibility: visible;
  pointer-events: auto;
  transition-delay: 0s;
}

/* The ground the panel lies on: clicking the carpet puts the drawer away. */
.cart__ground {
  position: absolute;
  inset: 0;
  /* Warm and light: the carpet has to stay a carpet behind the panel, not turn
     into a grey wall. */
  background: rgba(40, 28, 18, 0.3);
  opacity: 0;
  transition: opacity 0.32s ease;
}

.cart--open .cart__ground {
  opacity: 1;
}

/* Opaque, like the other long-form plates: 0.94 lets the weave read straight
   through a column this tall. */
.cart__panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100%);
  display: flex;

  background: #faf6f0;
  border-left: 1px solid rgba(58, 43, 30, 0.34);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.6),
    0 12px 26px -14px rgba(50, 35, 20, 0.65);

  transform: translateX(100%);
  transition: transform 0.32s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.cart--open .cart__panel {
  transform: translateX(0);
}

/* The paired rule the plates are drawn with, held clear of the safe areas so
   the line does not run under the notch or the home bar. */
.cart__panel::before {
  content: '';
  position: absolute;
  inset:
    calc(env(safe-area-inset-top) + 6px)
    calc(env(safe-area-inset-right) + 6px)
    calc(env(safe-area-inset-bottom) + 6px)
    6px;
  border: 1px solid rgba(58, 43, 30, 0.22);
  border-radius: 1px;
  pointer-events: none;
  z-index: 1;
}

/* The panel's own safe-area padding: body's cannot reach a viewport-fixed box.
   A column, so the total can sit at the foot of the drawer rather than
   trailing the last shirt — see .cart__foot. */
.cart__inner {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding:
    calc(env(safe-area-inset-top) + 1.9rem)
    calc(env(safe-area-inset-right) + 1.6rem)
    calc(env(safe-area-inset-bottom) + 2.4rem)
    1.6rem;
}

.cart__head {
  position: relative;
  margin-bottom: 1.9rem;
  text-align: center;
}

.cart__title {
  color: #3a2b1e;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/* Out of flow, so the title stays centred on the panel rather than on whatever
   is left over beside the button. */
.cart__close {
  position: absolute;
  top: -0.6rem;
  right: -0.5rem;
  width: 44px;
  height: 44px;
  font: inherit;
  font-size: 1.6rem;
  line-height: 1;
  color: #3a2b1e;
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.25s ease;
}

.cart__close:hover,
.cart__close:focus-visible {
  color: #2e4057;
}

.cart__rule {
  margin-top: 0.7rem;
  color: rgba(58, 43, 30, 0.5);
  font-size: 0.6rem;
}

.cart__empty {
  color: rgba(58, 43, 30, 0.7);
  font-size: 0.92rem;
  letter-spacing: 0.02em;
}

.cart__list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
}

.cart__line {
  display: flex;
  gap: 1.1rem;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid rgba(58, 43, 30, 0.18);
}

.cart__line:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

/* Square and cropped, the way a contact sheet holds a shot — the photographs
   have their own backgrounds, so a frame is all they need. */
.cart__thumb {
  flex: none;
  width: 76px;
  height: 76px;
  object-fit: cover;
  border: 1px solid rgba(58, 43, 30, 0.28);
  border-radius: 2px;
}

.cart__detail {
  flex: 1;
  min-width: 0;
}

.cart__name {
  color: #3a2b1e;
  font-size: 0.98rem;
  font-weight: 600;
}

.cart__size {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.25rem;
  color: rgba(58, 43, 30, 0.65);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/* On the size's line rather than under the name: the row already carries a
   stepper and a remove button, and a fourth block would make the cart taller
   than the shirts in it. */
.cart__price {
  flex: none;
  color: #3a2b1e;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}

/* The whole row goes quiet while Shopify is answering for it — the buttons are
   already disabled, and this is what says why. */
.cart__line.is-busy {
  opacity: 0.55;
}

/* Stepper and remove on one line: stacked, they made every row three blocks
   tall and the column read as a form rather than as a list. */
.cart__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.cart__quantity {
  display: flex;
  align-items: center;
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-radius: 2px;
}

.cart__step {
  width: 34px;
  height: 34px;
  font: inherit;
  font-size: 1rem;
  color: #3a2b1e;
  background: none;
  border: 0;
  cursor: pointer;
}

.cart__step:disabled {
  color: rgba(58, 43, 30, 0.3);
  cursor: default;
}

.cart__count {
  min-width: 2ch;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #3a2b1e;
}

.cart__remove {
  /* Negative margin, not less padding: the target stays 44px tall while the
     label sits on the stepper's own line. */
  padding: 0.75rem 0;
  margin: -0.75rem 0;
  font: inherit;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(58, 43, 30, 0.62);
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.25s ease;
}

.cart__remove:hover,
.cart__remove:focus-visible {
  color: #2e4057;
}

.cart__remove:disabled,
.cart__step:disabled {
  cursor: default;
}

/* Pushed to the bottom of the drawer by the free space above it, so a cart
   holding one shirt does not leave the total floating in the middle of a tall
   empty panel. Once the list is long enough to fill the column the auto margin
   collapses and the foot simply follows the last line, which is where a
   scrolling cart wants it. */
.cart__foot {
  flex: none;
  margin-top: auto;
  padding-top: 1.6rem;
  border-top: 1px solid rgba(58, 43, 30, 0.28);
}

/* None of these may be squeezed to make room: in a flex column a long list
   would otherwise shrink its own rows instead of scrolling. */
.cart__head,
.cart__list,
.cart__empty,
.cart__error {
  flex: none;
}

.cart__sum {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  color: #3a2b1e;
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.cart__sum-value {
  font-size: 1.05rem;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}

.cart__note {
  margin-top: 0.5rem;
  color: rgba(58, 43, 30, 0.6);
  font-size: 0.74rem;
  line-height: 1.5;
}

/* The same filled button the product page leads with, so the one control that
   leaves this site looks the same in both places. */
.cart__checkout {
  display: block;
  margin-top: 1.3rem;
  padding: 0.95rem 1.6rem;

  color: #fdfaf6;
  text-decoration: none;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  background: #2e4057;
  border: 1px solid #2e4057;
  border-radius: 2px;
  box-shadow: 0 12px 24px -14px rgba(20, 30, 45, 0.9);
  transition: background-color 0.25s ease, border-color 0.25s ease;
}

.cart__checkout:hover,
.cart__checkout:focus-visible {
  background: #24344a;
  border-color: #24344a;
}

.cart__error {
  margin-top: 1.3rem;
  color: #2e4057;
  font-size: 0.82rem;
  line-height: 1.5;
}

.cart__close:focus-visible,
.cart__step:focus-visible,
.cart__remove:focus-visible,
.cart__checkout:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 3px;
  border-radius: 1px;
}

@media (prefers-reduced-motion: reduce) {
  .cart,
  .cart__ground,
  .cart__panel,
  .cart__close,
  .cart__remove,
  .cart__checkout {
    transition: none;
  }
}
</style>
